<template>
    <div>
        <v-card id="details" :loading="loading" height="100vh">
            <v-card-title ref="title">
                <v-btn small icon @click="close" color="primary" class="mr-2">
                    <v-icon>
                        mdi-close
                    </v-icon>
                </v-btn>
                <h3>
                    {{
                        $vuetify.breakpoint.smAndDown
                            ? `${originalOrder.code}`
                            : `Packing List (${originalOrder.code})`
                    }}
                    - {{ project.name }}
                </h3>
                <v-btn
                    v-if="packingList.rejectionReasons"
                    small
                    icon
                    @click="rejectionTable = true"
                    color="red"
                    class="ml-2"
                >
                    <v-icon>
                        mdi-book-cancel-outline
                    </v-icon>
                </v-btn>
                <v-btn
                    v-if="canToggleImportant"
                    small
                    icon
                    @click="toggleImportant"
                    :color="packingList.important ? 'red' : 'grey'"
                    class="ml-2"
                    :loading="loadingImportant"
                >
                    <v-icon>
                        {{
                            packingList.important
                                ? 'mdi-flag'
                                : 'mdi-flag-outline'
                        }}
                    </v-icon>
                </v-btn>
                <v-icon
                    v-else-if="packingList.important"
                    color="red"
                    class="ml-2"
                >
                    mdi-flag
                </v-icon>
                <v-spacer />
                <v-btn
                    small
                    icon
                    color="primary"
                    :disabled="loading"
                    @click="generatePackagesXLSX"
                    v-if="packingList.id"
                >
                    <v-icon>mdi-file-excel</v-icon>
                </v-btn>
                <v-btn small icon color="primary" @click="selectScopes">
                    <v-icon>mdi-printer-pos</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider class="ml-4 mr-5" />
            <v-card-text
                class="pt-0"
                id="virtual-scroll-table"
                :style="{ height: '81vh' }"
            >
                <v-form v-model="valid">
                    <v-row no-gutters class="mt-4" ref="projectSelector">
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="3"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="project.name"
                                prepend-icon="mdi-account-hard-hat"
                                label="Project"
                                readonly
                                disabled
                            />
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="3"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="deliveryInfoString"
                                hide-details
                                label="Delivery Information"
                                prepend-icon="mdi-map-marker"
                                @focus="openDeliveryInfoDialog"
                                @blur="resetFocus"
                                readonly
                                :disabled="loading || blockActions"
                            >
                            </v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="3"
                            class="pl-2"
                        >
                            <v-menu
                                ref="menu"
                                v-model="menuShipment"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="
                                            packingList.requestedShipmentDate
                                        "
                                        label="Requested Date For Shipment"
                                        prepend-icon="mdi-calendar-blank"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        hide-details
                                        :disabled="loading || blockActions"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="packingList.requestedShipmentDate"
                                    @input="
                                        updatePackingList({
                                            requestedShipmentDate:
                                                packingList.requestedShipmentDate,
                                        })
                                    "
                                    :min="
                                        !createPackingListPermission
                                            ? minDate
                                            : undefined
                                    "
                                >
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="3"
                            class="pl-2"
                        >
                            <v-menu
                                ref="menu"
                                v-model="menu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="packingList.scheduledFor"
                                        label="Scheduled For"
                                        prepend-icon="mdi-calendar-clock"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        required
                                        :rules="[rules.required]"
                                        :disabled="loading || blockActions"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="packingList.scheduledFor"
                                    @input="
                                        updatePackingList({
                                            scheduledFor:
                                                packingList.scheduledFor,
                                        })
                                    "
                                    :min="
                                        !createPackingListPermission
                                            ? minDate
                                            : undefined
                                    "
                                >
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-0" ref="projectSelector">
                        <v-col
                            cols="12"
                            xl="6"
                            lg="6"
                            md="6"
                            sm="6"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="packingList.notes"
                                rows="1"
                                hide-details
                                label="Notes"
                                prepend-icon="mdi-text"
                                class="mx-2"
                                readonly
                                disabled
                            >
                            </v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="3"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="createdBy"
                                prepend-icon="mdi-account"
                                label="Created By"
                                readonly
                                disabled
                            />
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="3"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="packingList.container"
                                rows="1"
                                hide-details
                                label="Container"
                                prepend-icon="mdi-package"
                                @blur="
                                    updatePackingList({
                                        container: packingList.container,
                                    })
                                "
                                :disabled="
                                    (!createPackingListPermission &&
                                        !schedulingOptions) ||
                                        blockActions
                                "
                            >
                                <template v-slot:append>
                                    <v-tooltip bottom>
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-btn
                                                icon
                                                @click="generateTempContainer"
                                                small
                                                tabindex="-1"
                                                v-bind="attrs"
                                                v-on="on"
                                                :disabled="
                                                    !schedulingOptions ||
                                                        loading ||
                                                        blockActions
                                                "
                                            >
                                                <v-icon color="primary"
                                                    >mdi-reload</v-icon
                                                >
                                            </v-btn>
                                        </template>
                                        <span
                                            >Generate temporal random
                                            container</span
                                        >
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-0" ref="projectSelector">
                        <v-col
                            cols="12"
                            xl="6"
                            lg="6"
                            md="6"
                            sm="6"
                            class="pl-2"
                        >
                            <v-combobox
                                v-model="packingList.selectedWorkOrders"
                                :items="projectWorkOrders"
                                item-text="code"
                                label="Work Orders"
                                prepend-icon="mdi-hammer-wrench"
                                multiple
                                chips
                                small-chips
                                readonly
                                hide-details
                                disabled
                            ></v-combobox>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="6"
                            lg="6"
                            md="6"
                            sm="6"
                            class="pl-2"
                        >
                            <v-combobox
                                v-model="packingList.selectedBOMs"
                                :items="projectBOMs"
                                item-text="code"
                                label="BOMs"
                                prepend-icon="mdi-list-box-outline"
                                multiple
                                chips
                                small-chips
                                readonly
                                hide-details
                                disabled
                            ></v-combobox>
                        </v-col>
                    </v-row>
                </v-form>
                <v-row ref="files" class="px-3 py-2">
                    <v-col cols="12" xl="2" lg="2" md="2" class="pl-2 mt-0">
                        <v-row
                            no-gutters
                            class="d-flex align-center mt-0 mb-1 ml-1"
                        >
                            <v-simple-checkbox
                                color="primary"
                                v-model="packingList.airShipment"
                                :disabled="
                                    loading ||
                                        packingList.status != 'pending' ||
                                        blockActions
                                "
                                @click="
                                    updatePackingList({
                                        airShipment: packingList.airShipment,
                                    })
                                "
                            ></v-simple-checkbox>
                            <p class="ma-0 pa-0">Air Shipment</p>
                        </v-row>
                        <v-row
                            no-gutters
                            class="d-flex align-center mt-0 mb-1 ml-1"
                        >
                            <v-simple-checkbox
                                color="primary"
                                v-model="packingList.blockActionsFromPMs"
                                :disabled="loading || !planeationPermission"
                                @input="
                                    updateBlockActionsFromPms({
                                        blockActionsFromPMs:
                                            packingList.blockActionsFromPMs,
                                    })
                                "
                            ></v-simple-checkbox>
                            <p class="ma-0 pa-0">
                                Block outside actions
                            </p>
                        </v-row>
                    </v-col>
                    <v-col cols="4" class="pt-0">
                        <div class="d-flex my-2">
                            <div class="d-flex align-center">
                                <strong>ATTACHED: </strong>
                            </div>
                            <div class="d-flex align-center flex-wrap">
                                <v-btn
                                    color="transparent"
                                    elevation="0"
                                    small
                                    class="ma-1 text-capitalize text--darken-2  pa-2"
                                    v-for="(file, i) in packingList.attachments"
                                    :key="i"
                                >
                                    <v-icon
                                        class="mr-1"
                                        color="primary"
                                        :style="{ 'font-size': '17px' }"
                                    >
                                        mdi-paperclip
                                    </v-icon>
                                    <span
                                        @click="openFile(file)"
                                        :style="{
                                            'font-size': '11px',
                                            color: '#2b81d6',
                                            'text-decoration': 'underline',
                                            cursor: 'pointer',
                                        }"
                                        >{{ file }}</span
                                    >
                                </v-btn>
                            </div>
                        </div>
                    </v-col>
                </v-row>
                <v-container fluid px-0>
                    <v-row>
                        <v-col
                            cols="12"
                            v-for="(packageElement,
                            packageIndex) in packingList.packages"
                            :key="packageIndex"
                            class="mb-5"
                        >
                            <v-data-table
                                :headers="headers"
                                :items="packageElement.items"
                                class="elevation-0"
                                :mobile-breakpoint="0"
                                disable-pagination
                                hide-default-footer
                                multiple-expand
                                :expanded.sync="packageElement.items"
                            >
                                <template v-slot:top>
                                    <v-row
                                        no-gutter
                                        :style="{
                                            'background-color': '#eeeeee',
                                        }"
                                        class="mt-0 mx-0"
                                    >
                                        <v-col cols="12" class="d-flex">
                                            <h2 class="mt-1">
                                                Package
                                                {{ packageIndex + 1 }}
                                            </h2>
                                            <h4
                                                class="d-flex align-end pl-2 pb-1"
                                                :style="{
                                                    'min-width': 'fit-content',
                                                }"
                                            >
                                                {{
                                                    `(W${
                                                        packageElement
                                                            .packageDimensions
                                                            .width
                                                    } x H${
                                                        packageElement
                                                            .packageDimensions
                                                            .height
                                                    } x L${
                                                        packageElement
                                                            .packageDimensions
                                                            .depth
                                                    } ${
                                                        packageElement
                                                            .packageDimensions
                                                            .units
                                                    }) - Crate Mass: ${
                                                        packageElement.packageDimensions &&
                                                        packageElement
                                                            .packageDimensions
                                                            .mass
                                                            ? Number(
                                                                  packageElement
                                                                      .packageDimensions
                                                                      .mass || 0
                                                              ).toFixed(1)
                                                            : 0
                                                    } Kg `
                                                }}<v-btn
                                                    x-small
                                                    icon
                                                    color="primary"
                                                    class="ml-1"
                                                    v-if="
                                                        project &&
                                                            project.projectManager &&
                                                            user.id ==
                                                                project.projectManager &&
                                                            ![
                                                                'completed',
                                                                'deleted',
                                                            ].includes(
                                                                packingList.status
                                                            )
                                                    "
                                                    @click="
                                                        openUpdatePackageParams(
                                                            packageElement
                                                        )
                                                    "
                                                    :disabled="
                                                        loading || blockActions
                                                    "
                                                >
                                                    <v-icon
                                                        >mdi-pencil</v-icon
                                                    > </v-btn
                                                >{{
                                                    `-  BOM:${
                                                        packageElement.packageBOM
                                                            ? packageElement.packageBOM
                                                            : ''
                                                    }`
                                                }}
                                            </h4>
                                            <v-spacer />
                                            <v-btn
                                                small
                                                icon
                                                @click="
                                                    printPackage(
                                                        packageElement,
                                                        packageIndex + 1
                                                    )
                                                "
                                            >
                                                <v-icon>mdi-printer-pos</v-icon>
                                            </v-btn>
                                            <!-- <v-btn
                                                v-if="
                                                    readAlllPackingListPermission
                                                "
                                                small
                                                icon
                                                :disabled="loading"
                                                @click="
                                                    openEditDialog(
                                                        packageElement
                                                    )
                                                "
                                            >
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn> -->
                                        </v-col>
                                    </v-row>
                                </template>
                                <!-- ITEMS -->
                                <template v-slot:[`item.type`]="{ item }">
                                    <p class="my-0">
                                        {{
                                            item['bom'] != undefined
                                                ? 'BOM'
                                                : 'WorkOrder'
                                        }}
                                    </p>
                                </template>
                                <template v-slot:[`item.code`]="{ item }">
                                    <p class="my-0">
                                        {{ item.workOrder || item.bom }}
                                        {{
                                            woData[item.workOrder] &&
                                            woData[item.workOrder].reprocess
                                                ? '(Reprocess)'
                                                : ''
                                        }}
                                    </p>
                                </template>
                                <template v-slot:[`item.partNumber`]="{ item }">
                                    <p class="my-0">
                                        {{ item.partNumber || item.code }}
                                    </p>
                                </template>
                                <template
                                    v-slot:[`item.finishAndUCCode`]="{ item }"
                                >
                                    <p class="my-0">
                                        <v-tooltip right>
                                            <template v-slot:activator="{ on }">
                                                <span
                                                    v-on="on"
                                                    style="cursor: pointer;"
                                                >
                                                    {{
                                                        replaceItem(
                                                            item,
                                                            'description'
                                                        )
                                                    }}
                                                </span>
                                            </template>
                                            <span>
                                                {{ replaceItem(item, 'code') }}
                                            </span>
                                        </v-tooltip>
                                    </p>
                                </template>
                                <template v-slot:[`item.mass`]="{ item }">
                                    <p class="my-0 text-right">
                                        {{ Number(item.mass || 0).toFixed(1) }}
                                        Kg
                                    </p>
                                </template>
                                <template
                                    v-slot:[`item.packingMass`]="{ item }"
                                >
                                    <p class="my-0 text-right">
                                        {{
                                            (
                                                item.mass * item.packingQty
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </p>
                                </template>
                                <!--FOOTER-->
                                <template v-slot:[`body.append`]>
                                    <tr
                                        :style="{
                                            'background-color': '#eeeeee',
                                        }"
                                    >
                                        <td
                                            v-for="(header,
                                            colIndex) in headers"
                                            :key="colIndex"
                                            :class="{
                                                'font-weight-bold': [
                                                    'packingQty',
                                                    'packingMass',
                                                    'notes',
                                                ].includes(header.value),
                                            }"
                                            :style="
                                                [
                                                    'packingQty',
                                                    'packingMass',
                                                    'notes',
                                                ].includes(header.value)
                                                    ? { 'text-align': 'right' }
                                                    : {}
                                            "
                                        >
                                            <template
                                                v-if="
                                                    header.value ===
                                                        'packingQty'
                                                "
                                            >
                                                {{
                                                    workOrderTotalPacking(
                                                        packageElement
                                                    )
                                                }}
                                            </template>
                                            <template
                                                v-else-if="
                                                    header.value ===
                                                        'packingMass'
                                                "
                                            >
                                                {{
                                                    workOrderTotalMass(
                                                        packageElement
                                                    ).toFixed(1)
                                                }}
                                                Kg
                                            </template>
                                            <template
                                                v-else-if="
                                                    header.value === 'notes'
                                                "
                                            >
                                                Gross Mass:
                                                {{
                                                    (
                                                        (+packageElement
                                                            .packageDimensions
                                                            ?.mass || 0) +
                                                        +packageElement.items.reduce(
                                                            (acc, item) =>
                                                                acc +
                                                                (item.packingMass ||
                                                                    0),
                                                            0
                                                        )
                                                    ).toFixed(1)
                                                }}
                                                Kg
                                                <v-btn
                                                    x-small
                                                    icon
                                                    color="primary"
                                                    class="ml-1"
                                                    v-if="
                                                        project &&
                                                            project.projectManager &&
                                                            user.id ==
                                                                project.projectManager &&
                                                            ![
                                                                'completed',
                                                                'deleted',
                                                            ].includes(
                                                                packingList.status
                                                            )
                                                    "
                                                    @click="
                                                        openUpdatePackageGrossMass(
                                                            packageElement
                                                        )
                                                    "
                                                    :disabled="
                                                        loading || blockActions
                                                    "
                                                >
                                                    <v-icon>mdi-pencil</v-icon>
                                                </v-btn>
                                            </template>
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-container>
                <v-row
                    no-gutters
                    :style="{ 'background-color': '#eeeeee' }"
                    class="mt-8"
                    ref="totals"
                >
                    <v-col cols="12" xl="1" lg="1" md="1">
                        <p class="my-2 font-weight-black ml-4">TOTALS</p>
                    </v-col>
                    <v-col cols="12" xl="3" lg="3" md="3">
                        <p class="my-2  ml-4 font-weight-black">
                            QUANTITY OF PACKAGES:
                            {{ packingList.packages.length }}
                        </p>
                    </v-col>
                    <v-col cols="12" xl="3" lg="3" md="3">
                        <p class="my-2  ml-4 font-weight-black">
                            QUANTITY OF ITEMS: {{ totalItemsQty() }}
                        </p>
                    </v-col>
                    <v-col cols="12" xl="3" lg="3" md="3">
                        <p class="my-2  ml-4 font-weight-black">
                            NET MASS: {{ netMass().toFixed(1) }} Kg
                        </p>
                    </v-col>
                    <v-col cols="12" xl="2" lg="2" md="2">
                        <p class="my-2  ml-4 font-weight-black">
                            GROSS MASS: {{ grossMass().toFixed(1) }} Kg
                        </p>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions ref="actions">
                <v-btn
                    color="red"
                    rounded
                    class="mt-n2 mr-3 white--text"
                    :loading="loading"
                    @click="rejectForm = true"
                    :disabled="loading || blockActions"
                >
                    REJECT
                </v-btn>
                <v-spacer />
                <v-btn
                    color="primary"
                    rounded
                    class="mt-n2 mr-3 white--text"
                    :loading="loading"
                    @click="openBalanceWarning"
                    :disabled="!valid || loading || blockActions"
                >
                    MARK AS SCHEDULED
                </v-btn>
            </v-card-actions>
        </v-card>
        <!--Reject Work Orders-->
        <v-dialog
            v-model="rejectForm"
            persistent
            max-width="600px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="rejectForm">
                <v-card-title class="text-h5">Reject</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            Write the reasons why you decide to reject this
                            packing list
                        </v-col>
                    </v-row>
                    <v-form v-model="valid">
                        <v-row no-gutters class="mb-2 mt-n5">
                            <v-col cols="12">
                                <v-textarea
                                    v-model="rejectedNotes"
                                    rows="2"
                                    hide-details
                                    label="Notes"
                                    prepend-icon="mdi-text"
                                    required
                                    :rules="[rules.required]"
                                    :disabled="loading || blockActions"
                                >
                                </v-textarea>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="rejectForm = false">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="rejectPackingList"
                        text
                        color="error"
                        :loading="loading"
                        :disabled="!valid || loading || blockActions"
                    >
                        Reject
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--DeliveryInfo-->
        <v-dialog persistent max-width="500px" v-model="deliveryInfoDialog">
            <DeliveryInfoForm
                v-if="deliveryInfoDialog"
                :packingList="packingList"
                :clientId="project.client"
                @replaceDeliveryInfo="setDeliveryInfo"
                @closeDialog="closeDeliveryInfoDialog"
            />
        </v-dialog>
        <!--Rejected-->
        <v-dialog persistent max-width="600px" v-model="rejectionTable">
            <RejectedPackingListTable
                v-if="rejectionTable"
                :items="packingList.rejectionReasons"
                @closeDialog="rejectionTable = false"
            />
        </v-dialog>
        <!--PACKAGE FORM-->
        <v-dialog
            v-model="packageForm"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <PackageForm
                v-if="packageForm"
                :projectWorkOrders="projectWorkOrders"
                :BOMs="projectBOMs"
                :measureUnits="measureUnits"
                :editionView="editionView"
                :originalPackage="selectedPackage"
                :packingList="packingList"
                :onlySuggested="this.onlySuggested"
                @close="closePackageForm"
                @replacePackage="replacePackage"
                @recalculateBalances="recalculateBalances"
            />
        </v-dialog>
        <!--Scopes Select-->
        <v-dialog persistent max-width="500px" v-model="openScopeSelection">
            <ScopesSelect
                v-if="openScopeSelection"
                :wos="scopesWO"
                @print="print"
                @closeDialog="openScopeSelection = false"
            />
        </v-dialog>
        <v-dialog persistent max-width="250px" v-model="updatePackageParams">
            <UpdatePackageParams
                v-if="updatePackageParams"
                :projectId="project.id"
                :packageElement="selectedPackage"
                :packingListId="packingList.id"
                @closeDialog="closeUpdatePackageParams"
            />
        </v-dialog>
        <!--Balance Warning-->
        <v-dialog persistent max-width="800px" v-model="balanceWarning">
            <BalanceWarning
                v-if="balanceWarning"
                :packingList="packingList"
                :packingItems="packingItems"
                :projectPackingLists="projectPackingLists"
                :projectWorkOrders="projectWorkOrders"
                :projectBOMs="projectBOMs"
                @markPLAsScheduled="markPLAsScheduled"
                @closeDialog="closeBalanceWarning"
            />
        </v-dialog>
        <v-dialog persistent max-width="450px" v-model="updatePackageGrossMass">
            <UpdatePackageGrossMass
                v-if="updatePackageGrossMass"
                :projectId="project.id"
                :packageElement="selectedPackage"
                :packingListId="packingList.id"
                :onlySuggested="onlySuggested"
                @closeDialog="closeUpdatePackageGrossMass"
            />
        </v-dialog>
        <!--Error Alert-->
        <Errors />
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'
import moment from 'moment'
import { openFile } from '@/services/storage/openFile.js'
import { printPackingPDF } from '@/components/Packing/packingPDF.js'
import { printPackagePDF } from './packagePDF'
import { v4 as uuidv4 } from 'uuid'
import WOMethods from '@/components/WorkOrders/WOMethods.js'
import { exportPackagesToExcel } from './PackagesXLSX.js'
import packingListRealtime from '@/components/Packing/mixins/packingListRealtime'

export default {
    name: 'ProcessPackingPending',
    mixins: [packingListRealtime],
    props: {
        originalOrder: {
            type: Object,
            required: false,
            default: () => {},
        },
        project: {
            type: Object,
            required: true,
            default: () => {},
        },
        onlySuggested: {
            type: Boolean,
            required: true,
            default: () => true,
        },
    },
    components: {
        Errors: () => import('@/components/Layout/Errors'),
        RejectedPackingListTable: () =>
            import('@/components/Packing/RejectedPackingListTable.vue'),
        PackageForm: () => import('@/components/Packing/PackageForm.vue'),
        DeliveryInfoForm: () =>
            import('@/components/Packing/DeliveryInfoForm.vue'),
        ScopesSelect: () => import('@/components/Packing/ScopesSelect.vue'),
        UpdatePackageParams: () =>
            import('@/components/Packing/UpdatePackageParams.vue'),
        BalanceWarning: () => import('@/components/Packing/BalanceWarning.vue'),
        UpdatePackageGrossMass: () =>
            import('@/components/Packing/UpdatePackageGrossMass.vue'),
    },
    data: () => ({
        packingList: {
            packages: [],
        },
        loading: false,
        projectWorkOrders: [],
        projectBOMs: [],
        measureUnits: [],
        headers: [
            {
                text: 'WORK ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'TYPE',
                value: 'type',
                align: 'center',
                sortable: false,
            },

            {
                text: 'ITEM CODE',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'FINISH & UC CODE',
                value: 'finishAndUCCode',
                align: 'center',
                sortable: false,
            },
            {
                text: 'UNIT MASS',
                value: 'mass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING QTY',
                value: 'packingQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING MASS',
                value: 'packingMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
            {
                text: 'OC',
                value: 'oc',
                align: 'left',
                sortable: false,
            },
            {
                text: 'POSITION',
                value: 'costPosition',
                align: 'left',
                sortable: false,
            },
        ],
        valid: false,
        menu: false,
        menuShipment: false,
        rules: {
            required: v => !!v || 'Required',
        },
        rejectForm: false,
        rejectedNotes: null,
        createdBy: '',
        companyId: JSON.parse(localStorage.getItem('company')),
        users: [],
        rejectionTable: false,
        deliveryInfoString: '',
        readAlllPackingListPermission: false,
        editionView: false,
        selectedPackage: null,
        packageForm: false,
        enableFocus: true,
        deliveryInfoDialog: false,
        selectedProcess: { id: 'readyForPacking', name: 'Ready for packing' },
        settingFinish: undefined,
        openScopeSelection: false,
        scopesWO: null,
        packingProcess: { id: 'packing', name: 'Packing' },
        qualityProcess: { id: 'quality', name: 'Quality' },
        user: {},
        updatePackageParams: false,
        updatePackageGrossMass: false,
        projectPackingLists: [],
        packingItems: [],
        woData: {},
        balanceWarning: false,
        schedulingOptions: false,
        loadingImportant: false,
        createPackingListPermission: false,
        planeationPermission: false,
        woMethods: new WOMethods(),
    }),
    computed: {
        // Block actions if packing list has blockActionsFromPMs and user doesn't have planning permissions
        blockActions() {
            return (
                !!this.packingList.blockActionsFromPMs &&
                !this.planeationPermission
            )
        },
        canToggleImportant() {
            // Only production planners can toggle important flag
            return (
                this.user.permissions &&
                this.user.permissions.includes('updateWOProcesses')
            )
        },
        minDate() {
            const createdOnDate = moment.unix(
                this.originalOrder.createdOn._seconds ||
                    this.originalOrder.createdOn.seconds
            )

            // Add 3 weeks to the created on date
            const minDate = createdOnDate.add(3, 'weeks')

            // Return in YYYY-MM-DD format
            return minDate.format('YYYY-MM-DD')
        },
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { users },
            } = await API.getUsers()
            this.users = users
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            this.realtimeCurrentUserId = user.id
            this.schedulingOptions = this.user.permissions.includes(
                'updateWOProcesses'
            )
            this.readAlllPackingListPermission = user.permissions.includes(
                'readAllPackingLists'
            )
            // set create packing list permission
            this.createPackingListPermission = this.user.permissions.includes(
                'createPackingList'
            )
            this.planeationPermission = !!(
                this.createPackingListPermission || this.schedulingOptions
            )

            //get project packing lists
            this.projectPackingLists = await API.getPackingListsByProject({
                projectId: this.project.id,
            })

            if (this.originalOrder) {
                this.packingList = _.cloneDeep(this.originalOrder)
                this.createdBy = this.findUserName(this.packingList.createdBy)
                if (this.packingList.deliveryInformation) {
                    this.deliveryInfoString =
                        this.packingList.deliveryInformation.location +
                        ' - ' +
                        this.packingList.deliveryInformation.contact +
                        ' - ' +
                        this.packingList.deliveryInformation.phone
                }
            }
            this.projectWorkOrders = await API.getWorkOrdersByProject({
                projectId: this.project.id,
                includeClosed: false,
            })

            const allProjectWorOrders = await API.getWorkOrdersByProject({
                projectId: this.project.id,
                includeClosed: true,
            })

            //TODO: verify
            //this.projectWorkOrders = this.allProjectWorOrders.filter(w => w.status !== 'closed' && w.status !== 'cancelled')

            allProjectWorOrders.forEach(workOrder => {
                // get wo data by code
                if (workOrder.code && !this.woData[workOrder.code]) {
                    this.woData[workOrder.code] = workOrder
                }
            })

            this.projectBOMs = await API.getBOMsChangelog({
                projectId: this.project.id,
                status: ['approved', 'closed'],
            })
            const {
                data: { settings },
            } = await API.getSettings()
            const settingIndex = settings.findIndex(u => u.name === 'Quotes')
            this.settingFinish = settings.find(s => s.name == 'FinishAndUCCode')
            if (settingIndex > -1) {
                this.measureUnits = settings[settingIndex].measureUnits
                    .filter(u => u.measure == 'length')
                    .map(unit => unit.abbr)
            }
            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()

            this.setupProjectPackingListsListener()
            if (this.originalOrder) {
                this.setupCurrentPackingListListener()
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        close() {
            this.$emit('close')
        },
        async toggleImportant() {
            try {
                this.loadingImportant = true
                const newImportantValue = !this.packingList.important
                await API.updatePackingList({
                    projectId: this.project.id,
                    packingListId: this.packingList.id,
                    important: newImportantValue,
                })
                // Update local state
                this.packingList.important = newImportantValue
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingImportant = false
            }
        },
        formatWorkOrders() {
            this.projectWorkOrders.forEach(workOrder => {
                const wo = this.woMethods.formatItems(workOrder)
                //calculate available qty
                if (wo.items) {
                    wo.items.forEach(item => {
                        //filter the packing items for this item and wo
                        const packingRegisters = this.packingItems.filter(
                            packingItem =>
                                packingItem.workOrder == workOrder.code &&
                                packingItem.partNumber == item.partNumber
                        )
                        let totalPacked = 0
                        packingRegisters.forEach(register => {
                            //packed
                            totalPacked += register.packingQty
                        })
                        const available = this.calculateAvailableQty(item)
                        item.availableQty = available - totalPacked
                    })
                }
            })
        },
        formatBOMs() {
            this.projectBOMs.forEach(bom => {
                //calculate available qty
                if (bom.items) {
                    bom.items.forEach(item => {
                        //filter the packing items for this item and bom
                        const packingRegisters = this.packingItems.filter(
                            packingItem =>
                                packingItem.bom == bom.code &&
                                packingItem.code == item.code
                        )
                        let totalPacked = 0
                        packingRegisters.forEach(register => {
                            //packed
                            totalPacked += register.packingQty
                        })
                        const available = this.calculateAvailableBOMQty(item)
                        item.availableQty = available - totalPacked
                    })
                }
            })
        },

        formatPackingItems() {
            this.packingItems = []
            this.projectPackingLists.forEach(pl => {
                if (pl.packages && pl.code != this.packingList.code) {
                    pl.packages.forEach(packageElement => {
                        if (packageElement.items) {
                            packageElement.items.forEach(item => {
                                let formattedItem = {
                                    suggestedQty: +item.suggestedQty || 0,
                                    packingQty: +item.packingQty || 0,
                                    status: pl.status,
                                    packingList: pl.code,
                                }
                                if (item.workOrder) {
                                    formattedItem = {
                                        partNumber: item.partNumber,
                                        partNumberId: item.partNumberId,
                                        workOrder: item.workOrder,
                                        costPosition: item.costPosition || '',
                                        description: item.description || '',
                                        finishAndUCCode:
                                            item.finishAndUCCode || '',
                                        mass: item.mass || 0,
                                        notes: item.notes || '',
                                        oc: item.oc || '',
                                        quantity: item.quantity,
                                        ...formattedItem,
                                    }
                                } else if (item.bom) {
                                    formattedItem = {
                                        code: item.code,
                                        bom: item.bom,
                                        ...formattedItem,
                                    }
                                }
                                this.packingItems.push(formattedItem)
                            })
                        }
                    })
                }
            })
            //add the current pl items
            if (this.packingList && this.packingList.packages) {
                this.packingList.packages.forEach(packageElement => {
                    if (packageElement.items) {
                        packageElement.items.forEach(item => {
                            let formattedItem = {
                                suggestedQty: +item.suggestedQty || 0,
                                packingQty: +item.packingQty || 0,
                                status: this.packingList.status,
                                packingList: this.packingList.code,
                            }
                            if (item.workOrder) {
                                formattedItem = {
                                    partNumber: item.partNumber,
                                    partNumberId: item.partNumberId,
                                    workOrder: item.workOrder,
                                    costPosition: item.costPosition || '',
                                    description: item.description || '',
                                    finishAndUCCode: item.finishAndUCCode || '',
                                    mass: item.mass || 0,
                                    notes: item.notes || '',
                                    oc: item.oc || '',
                                    quantity: item.quantity,
                                    ...formattedItem,
                                }
                            } else if (item.bom) {
                                formattedItem = {
                                    code: item.code,
                                    bom: item.bom,
                                    ...formattedItem,
                                }
                            }
                            this.packingItems.push(formattedItem)
                        })
                        // sort packages items in the order of the partNumbers
                        if (this.onlySuggested) {
                            packageElement.items.sort((a, b) => {
                                const numA =
                                    parseInt(a.partNumber?.slice(-4), 10) || 0
                                const numB =
                                    parseInt(b.partNumber?.slice(-4), 10) || 0
                                return numA - numB
                            })
                        }
                    }
                })
            }
        },
        replaceItem(item, option) {
            if (item.finishAndUCCode) {
                if (this.settingFinish) {
                    let dataFinishAndUCCode = this.settingFinish.finishes.find(
                        e => e.id == item.finishAndUCCode
                    )
                    if (option == 'description') {
                        return (
                            dataFinishAndUCCode &&
                            dataFinishAndUCCode.description
                        )
                    } else if (option == 'code') {
                        return dataFinishAndUCCode && dataFinishAndUCCode.name
                    }
                }
            }
        },
        totalItemsQty() {
            return this.packingList.packages.reduce(
                (accumulator, packageInfo) =>
                    accumulator +
                    packageInfo.items.reduce(
                        (itemAccumulator, item) =>
                            itemAccumulator + Number(item.packingQty),
                        0
                    ),
                0
            )
        },
        netMass() {
            return this.packingList.packages.reduce(
                (accumulator, packageInfo) =>
                    accumulator +
                    packageInfo.items.reduce(
                        (itemAccumulator, item) =>
                            itemAccumulator + Number(item.packingMass),
                        0
                    ),
                0
            )
        },
        grossMass() {
            const netMass = this.netMass()
            const cratesMass = this.packingList.packages.reduce(
                (accumulator, packageInfo) =>
                    accumulator + (+packageInfo.packageDimensions.mass || 0),

                0
            )
            return netMass + cratesMass
        },
        workOrderTotalPacking(packageElement) {
            return packageElement.items.reduce(
                (itemAccumulator, item) =>
                    itemAccumulator + Number(item.packingQty),
                0
            )
        },
        workOrderTotalMass(packageElement) {
            return packageElement.items.reduce(
                (itemAccumulator, item) =>
                    itemAccumulator + Number(item.packingMass),
                0
            )
        },
        findUserName(id) {
            let userName = ''
            const user = this.users.find(u => u.id == id)
            if (user) {
                userName = user.name
            }
            return userName
        },
        openDeliveryInfoDialog() {
            if (this.enableFocus) {
                this.deliveryInfoDialog = true
                this.$nextTick(() => {
                    this.enableFocus = false
                    this.$refs.deliveryInfoInput.blur()
                })
            }
        },
        resetFocus() {
            this.enableFocus = true
        },
        closeDeliveryInfoDialog() {
            this.enableFocus = false
            this.deliveryInfoDialog = false
        },
        setDeliveryInfo(deliveryInfo) {
            this.packingList.deliveryInformation = deliveryInfo
            this.deliveryInfoString =
                deliveryInfo.location +
                ' - ' +
                deliveryInfo.contact +
                ' - ' +
                deliveryInfo.phone
            this.$emit('replacePackingList', this.packingList)
        },

        async markPLAsScheduled(selectedWorkOrders) {
            try {
                this.loading = true
                const {
                    updatedPackingList,
                    newPackingList,
                } = await API.markPLAsScheduled({
                    projectId: this.project.id,
                    packingListId: this.originalOrder.id,
                    scheduledFor: this.packingList.scheduledFor,
                    selectedWorkOrders,
                })
                this.$emit('replacePackingList', updatedPackingList)
                this.$emit('addPackingList', newPackingList)
                this.close()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async openFile(file) {
            try {
                this.loading = true
                await openFile(
                    file,
                    `${this.companyId}/projects/${this.packingList.projectId}/packingLists`
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async rejectPackingList() {
            try {
                this.loading = true
                const packingList = await API.rejectPackingList({
                    projectId: this.project.id,
                    packingListId: this.originalOrder.id,
                    notes: this.rejectedNotes,
                })
                this.$emit('replacePackingList', packingList)
                this.close()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async print(scopes) {
            try {
                this.loading = true
                this.packingList.scopes = scopes
                await printPackingPDF(this.packingList, this.project)
                this.openScopeSelection = false
            } catch (error) {
                console.log(error)
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        openEditDialog(packageData) {
            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()
            this.editionView = true
            this.selectedPackage = packageData
            this.packageForm = true
        },
        closePackageForm() {
            this.selectedPackage = null
            this.editionView = null
            this.packageForm = false
        },
        replacePackage({ packageData }) {
            const index = this.packingList.packages.findIndex(
                p => p.id == this.selectedPackage.id
            )
            this.packingList.packages.splice(index, 1, packageData)
            this.recalculateBalances()
            this.closePackageForm()
            let totalMass = 0
            if (
                this.packingList.packages &&
                this.packingList.packages.length > 0
            ) {
                this.packingList.packages.forEach(pck => {
                    totalMass += pck.items.reduce(
                        (accum, item) => accum + Number(item.packingMass || 0),
                        0
                    )
                })
            }
            this.packingList.totalMass = totalMass
            this.$emit('replacePackingList', this.packingList)
        },
        recalculateBalances() {
            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()
        },
        calculateAvailableQty(item) {
            let availableQty = 0

            if (item.production) {
                item.production.forEach(production => {
                    if (production.process.id == this.selectedProcess.id) {
                        availableQty += production.qty
                    }
                })
            }
            if (item.deliveries) {
                let received = item.deliveries.filter(
                    register =>
                        register.nextProcess.id == this.packingProcess.id &&
                        register.receivedBy
                )
                const totalReceived = received.reduce(
                    (total, register) => total + register.qty,
                    0
                )

                let released = 0

                if (item.processes.includes(this.qualityProcess.id)) {
                    if (item.quality) {
                        released = item.quality.reduce(
                            (total, q) => total + (q.releaseQty || 0),
                            0
                        )
                    }
                } else {
                    released = totalReceived
                }
                availableQty += Math.min(totalReceived, released)
            }
            return availableQty
        },
        calculateAvailableBOMQty(item) {
            let availableQty = 0
            if (item.deliveries) {
                item.deliveries.forEach(delivery => {
                    availableQty += Number(delivery.qty || 0)
                })
            }
            return availableQty
        },
        async generatePackagesXLSX() {
            try {
                this.loading = true
                await exportPackagesToExcel({
                    packingList: this.packingList,
                    headers: this.headers,
                    onlySuggested: this.onlySuggested,
                    settingFinish: this.settingFinish,
                    woData: this.woData,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async printPackage(packageElement, index) {
            try {
                this.loading = true
                await printPackagePDF(
                    this.packingList,
                    this.project,
                    packageElement,
                    index
                )
            } catch (error) {
                console.log(error)
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        selectScopes() {
            let wos = []
            this.packingList.selectedWorkOrders?.forEach(wo => {
                const foundWO = this.projectWorkOrders.find(
                    w => w.code == wo.code
                )
                if (foundWO) {
                    wos.push(foundWO)
                }
            })
            this.scopesWO = wos
            this.openScopeSelection = true
        },
        openUpdatePackageParams(packageElement) {
            this.updatePackageParams = true
            this.selectedPackage = _.cloneDeep(packageElement)
        },
        closeUpdatePackageParams(updatedParams) {
            //update params in the package
            if (Object.keys(updatedParams).length > 0) {
                const index = this.packingList.packages.findIndex(
                    p => p.id == this.selectedPackage.id
                )
                if (index > -1) {
                    this.packingList.packages[index] = {
                        ...this.packingList.packages[index],
                        ...updatedParams,
                    }
                    this.$emit('replacePackingList', this.packingList)
                }
            }
            this.updatePackageParams = false
            this.selectedPackage = {}
        },
        async generateTempContainer() {
            const code = uuidv4().substring(0, 5)
            const container = `TEMP-${code}`
            let auxPL = _.cloneDeep(this.packingList)
            auxPL.container = container

            this.packingList = _.cloneDeep(auxPL)
            await this.updatePackingList({
                container: this.packingList.container,
            })
        },
        async updatePackingList(params) {
            try {
                this.menu = false
                if (this.originalOrder) {
                    await API.updatePackingList({
                        projectId: this.packingList.projectId,
                        packingListId: this.packingList.id,
                        ...params,
                    })
                    this.$emit('replacePackingList', this.packingList)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async updateBlockActionsFromPms(params) {
            try {
                this.menu = false
                if (this.originalOrder) {
                    const updatedPackingList = await API.updateBlockActionsFromPms(
                        {
                            projectId: this.packingList.projectId,
                            packingListId: this.packingList.id,
                            ...params,
                        }
                    )
                    this.packingList.blockActionsFromPMs =
                        updatedPackingList.blockActionsFromPMs
                    this.$emit('replacePackingList', this.packingList)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async openBalanceWarning() {
            try {
                this.loading = true
                this.projectPackingLists = await API.getPackingListsByProject({
                    projectId: this.project.id,
                })
                this.formatPackingItems()
                this.balanceWarning = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeBalanceWarning() {
            this.balanceWarning = false
        },
        openUpdatePackageGrossMass(packageElement) {
            this.updatePackageGrossMass = true
            this.selectedPackage = _.cloneDeep(packageElement)
        },
        closeUpdatePackageGrossMass(updatedData) {
            if (Object.keys(updatedData).length > 0) {
                const index = this.packingList.packages.findIndex(
                    p => p.id == this.selectedPackage.id
                )
                if (index > -1) {
                    const newCrateMass =
                        updatedData.packageDimensions?.mass !== undefined
                            ? updatedData.packageDimensions.mass
                            : this.packingList.packages[index].packageDimensions
                                  .mass
                    this.$set(
                        this.packingList.packages[index].packageDimensions,
                        'mass',
                        newCrateMass
                    )

                    this.$emit('replacePackingList', this.packingList)
                }
            }
            this.updatePackageGrossMass = false
            this.selectedPackage = {}
        },
    },
}
</script>

<style scoped>
#virtual-scroll-table {
    width: 100%;
    overflow: auto;
}
.quote_tables {
    width: 100%;
}
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
}
</style>
